import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { TrackCard } from '@/components/TrackCard';
import { 
  Music2, Disc3, Sparkles, Flame, TrendingUp, Play, ArrowRight, 
  GraduationCap, Video, Crown, Calendar, Star, Drum, Radio, Users,
  CheckCircle, ChevronRight, ChevronLeft, Paintbrush, Volume2, ChevronDown,
  Shirt, Mic, BookOpen, Headphones, Flag
} from 'lucide-react';
import { motion, useInView } from 'framer-motion';
import { API_URL, getApiAssetUrl } from '@/config/api';
import { HomePillars } from '@/components/home/HomePillars';
import { HomeWhyChooseUs } from '@/components/home/HomeWhyChooseUs';
import { HomeTestimonials } from '@/components/home/HomeTestimonials';
import { HomeClients } from '@/components/home/HomeClients';
import { HomeTableOfContents } from '@/components/home/HomeTableOfContents';
import { HomePerformances } from '@/components/home/HomePerformances';
import { HomeThreeColumnServices } from '@/components/home/HomeThreeColumnServices';
import { CarnivalBackground } from '@/components/home/CarnivalBackground';
import { useHomepageConfig } from '@/hooks/useHomepageConfig';
import { EditModeProvider, useEditMode } from '@/context/EditModeContext';
import { EditToolbar } from '@/components/edit/EditToolbar';
import { Editable } from '@/components/edit/Editable';
import { AdminEditorLink } from '@/components/edit/AdminEditorLink';

const API = API_URL;

// Hero image - Casa Samba's 40+ year legacy (use centralized config)
const HERO_SLIDES = [
  '/api/media/casa-samba/library/images/hero/casa-1.jpg',
  '/api/media/casa-samba/library/images/hero/casa-2.jpg',
  '/api/media/casa-samba/library/images/hero/casa-3.jpg',
  '/api/media/casa-samba/library/images/hero/casa-4.jpg',
  '/api/media/casa-samba/library/images/hero/casa-5.jpg',
  '/api/media/casa-samba/library/images/hero/casa-6.jpg',
  '/api/media/casa-samba/library/images/hero/casa-7.jpg',
  '/api/media/casa-samba/library/images/hero/casa-8.jpg',
  '/api/media/casa-samba/library/images/hero/casa-9.jpg',
  '/api/media/casa-samba/library/images/hero/casa-10.jpg',
  '/api/media/casa-samba/library/images/hero/casa-11.jpg',
  '/api/media/casa-samba/library/images/hero/casa-12.jpg',
];

// Quick access feature cards
const QUICK_FEATURES = [
  { icon: GraduationCap, title: "Master Classes", desc: "38+ years of expertise", link: "/samba-lessons", color: "amber" },
  { icon: Drum, title: "Play Along", desc: "Interactive practice", link: "/play-along", color: "green" },
  { icon: Shirt, title: "Costume Shop", desc: "Handcrafted designs", link: "/costume-shop", color: "orange" },
  { icon: Mic, title: "Recording Studio", desc: "Remote sessions", link: "/recording-studio", color: "purple" },
  { icon: Video, title: "Mobile Studio", desc: "Multi-track recording", link: "/mobile-studio", color: "pink" },
  { icon: Crown, title: "Membership", desc: "Unlimited access", link: "/membership", color: "yellow" },
];

// Animated counter (simplified)
const Counter = ({ end, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  
  useEffect(() => {
    if (!isInView) return;
    const duration = 1500;
    const steps = 30;
    const increment = end / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [end, isInView]);
  
  return <span ref={ref}>{count}{suffix}</span>;
};

const HomeInner = () => {
  const [featuredTracks, setFeaturedTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [heroSlide, setHeroSlide] = useState(0);
  const [slides, setSlides] = useState(HERO_SLIDES.map(src => ({ image: src, caption: '' })));
  const cfg = useHomepageConfig();
  const hero = cfg?.hero || {};

  useEffect(() => {
    axios.get(`${API}/admin/hero-slides`)
      .then(res => {
        if (res.data?.slides?.length) setSlides(res.data.slides);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!slides.length) return undefined;
    const t = setInterval(() => setHeroSlide(s => (s + 1) % slides.length), 4000);
    return () => clearInterval(t);
  }, [slides.length]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const res = await axios.get(`${API}/tracks/featured`);
      setFeaturedTracks(res.data || []);
    } catch (error) {
      console.error('Error loading featured tracks:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="relative isolate overflow-hidden" data-testid="home-page">
      <CarnivalBackground />
      <HomeTableOfContents />
      
      {/* ==================== HERO SECTION - Casa Samba Legacy ==================== */}
      <section id="top" className="relative h-screen min-h-[600px] flex items-center justify-center overflow-hidden" data-testid="hero-section">
        {/* Background Slideshow - Casa Samba Performances */}
        <div className="absolute inset-0">
          {slides.map((s, i) => (
            <img
              key={`${s.display_image || s.image}-${i}`}
              src={s.display_image || s.image}
              alt="Escola de Samba Casa Samba performance"
              className={`absolute inset-0 w-full h-full object-cover ${i === heroSlide ? 'opacity-100' : 'opacity-0'}`}
              style={{ transition: 'opacity 2s ease-in-out' }}
              loading={i === 0 ? 'eager' : 'lazy'}
              decoding="async"
              data-testid={`hero-slide-${i}`}
            />
          ))}
          {/* Overlays for readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-transparent to-black/50" />
        </div>

        {/* Hero Content - raised up to leave room for the slide caption below */}
        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center pb-40 sm:pb-44 md:pb-40">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-8"
          >
            <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm">
              <Sparkles className="w-5 h-5 text-primary" />
              <Editable
                path="hero.badge"
                fallback={hero.badge || 'Fala comigo Gente'}
                className="text-sm md:text-base font-bold text-white uppercase tracking-widest"
              />
            </div>
          </motion.div>

          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black tracking-tighter mb-6"
          >
            <Editable as="span" path="hero.titleTop" fallback={hero.titleTop || 'Escola de Samba'} className="block text-white" />
            <Editable as="span" path="hero.titleBottom" fallback={hero.titleBottom || 'Casa Samba'} className="block text-primary" />
          </motion.h1>

          {/* Times-Picayune Quote */}
          <Editable
            as="p"
            path="hero.quote"
            fallback={hero.quote || '\u201cCasa Samba The Next Best Thing To a Trip To Brasil\u201d. The Times Picayune'}
            className="text-base md:text-lg text-white/70 italic mb-10"
            data-testid="hero-press-quote"
          />

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 flex-wrap"
          >
            <Link to={hero.btn1Href || '/booking'}>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-7 py-3.5 bg-primary text-primary-foreground rounded-full font-bold text-sm md:text-base shadow-lg shadow-primary/30"
                data-testid="hero-btn-quote"
              >
                <Editable path="hero.btn1Label" fallback={hero.btn1Label || 'Get a Quote'} />
              </motion.button>
            </Link>
            <Link to={hero.btn2Href || '/performances'}>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-7 py-3.5 bg-primary text-primary-foreground rounded-full font-bold text-sm md:text-base shadow-lg shadow-primary/30"
                data-testid="hero-btn-performances"
              >
                <Editable path="hero.btn2Label" fallback={hero.btn2Label || 'Types of Performances'} />
              </motion.button>
            </Link>
            <Link to={hero.btn3Href || '/classes'}>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-7 py-3.5 bg-primary text-primary-foreground rounded-full font-bold text-sm md:text-base shadow-lg shadow-primary/30"
                data-testid="hero-btn-classes"
              >
                <Editable path="hero.btn3Label" fallback={hero.btn3Label || 'Brasilian Drum and Dance Classes'} />
              </motion.button>
            </Link>
            <Link to="/registration">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{
                  opacity: 1,
                  scale: 1,
                  boxShadow: [
                    '0 0 0 0 rgba(251,146,60,0.5)',
                    '0 0 0 12px rgba(251,146,60,0)',
                  ],
                }}
                transition={{
                  boxShadow: { repeat: Infinity, duration: 1.8, ease: 'easeOut' },
                }}
                className="px-7 py-3.5 bg-gradient-to-r from-orange-500 via-red-500 to-yellow-500 text-white rounded-full font-bold text-sm md:text-base shadow-lg shadow-orange-500/40 border-2 border-yellow-300/60"
                data-testid="hero-btn-carnaval"
              >
                <Editable path="hero.btn4Label" fallback={'🎭 Mardi Gras Brasil 2026 — Register'} />
              </motion.button>
            </Link>
          </motion.div>
        </div>

        {/* Slide Caption - shows below the title area, editable in Site Manager */}
        {slides[heroSlide]?.caption ? (
          <div className="absolute bottom-16 md:bottom-20 left-1/2 -translate-x-1/2 z-10 w-[92%] max-w-2xl px-4" data-testid="hero-caption">
            <p
              key={heroSlide}
              className="text-center text-white text-xs sm:text-sm md:text-base font-medium px-4 py-2 rounded-xl bg-black/45 backdrop-blur-sm border border-white/15 leading-snug"
              style={{ textShadow: '0 2px 8px rgba(0,0,0,0.8)', animation: 'fadeIn 0.9s ease' }}
            >
              {slides[heroSlide].caption}
            </p>
          </div>
        ) : null}

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="flex flex-col items-center text-white/60"
          >
            <span className="text-xs uppercase tracking-widest mb-2">Discover</span>
            <ChevronDown className="w-5 h-5" />
          </motion.div>
        </motion.div>
      </section>

      {/* ==================== PILLARS - Experience / Professionalism / Unique ==================== */}
      <div id="pillars"><HomePillars /></div>

      {/* ==================== SECTION 3: TYPES OF PERFORMANCES & RECOMMENDATIONS ==================== */}
      <div id="what-we-do"><HomePerformances /></div>

      {/* ==================== SECTION 5: 3-COLUMN STAFF / SERVICES / CLASSES ==================== */}
      <div id="services-columns"><HomeThreeColumnServices /></div>

      {/* ==================== SECTION 6: WHY CHOOSE US (numbered list) ==================== */}
      <div id="book"><HomeWhyChooseUs /></div>

      {/* ==================== SECTION 7: TESTIMONIALS ==================== */}
      <div id="testimonials"><HomeTestimonials /></div>

      {/* ==================== SECTION 7 (cont): CLIENTS ==================== */}
      <div id="clients"><HomeClients /></div>
    </div>
  );
};

export const Home = () => (
  <EditModeProvider>
    <HomeInner />
    <EditToolbar />
    <AdminEditorLink to="/admin/dashboard#homepage-editor-btn" label="Edit Homepage" />
  </EditModeProvider>
);

export default Home;
